import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { X } from 'lucide-react-native';
import { AnimatePresence, MotiView } from 'moti';
import { memo, useEffect } from 'react';
import { Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BiDashboardToast } from '../functions/useBiDashboard';

const kShownMs = 8000;

/**
 * The bar after each write: what changed and 撤销, or what went wrong. It
 * offers undo only while that change is still the dashboard's latest.
 */
export const UndoToast = memo(
  ({
    toast,
    revision,
    canUndo = true,
    onUndo,
    onDismiss,
  }: {
    toast: BiDashboardToast | null;
    /** The dashboard's current revision. */
    revision: number;
    /** False while the undo cannot be sent, e.g. a chat reply is running. */
    canUndo?: boolean;
    onUndo: () => void;
    onDismiss: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    useEffect(() => {
      if (!toast) return;
      const timer = setTimeout(onDismiss, kShownMs);
      return () => clearTimeout(timer);
    }, [toast, onDismiss]);
    const undone = toast?.kind === 'undone' || toast?.kind === 'chat_undone';
    const text =
      toast && 'text' in toast ? toast.text : undone ? '已撤销上一次修改' : '';
    const action =
      canUndo && toast && toast.kind !== 'notice' && toast.revision === revision
        ? undone
          ? '恢复'
          : '撤销'
        : null;
    return (
      <View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: insets.bottom + rpx(20),
          alignItems: 'center',
          paddingHorizontal: rpx(16),
        }}
      >
        <AnimatePresence>
          {toast ? (
            <MotiView
              key="toast"
              from={{ opacity: 0, translateY: 12 }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: 12 }}
              transition={{ type: 'timing', duration: 180 }}
              accessibilityRole="alert"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(12),
                maxWidth: 560,
                paddingLeft: rpx(16),
                paddingRight: rpx(8),
                paddingVertical: rpx(8),
                borderRadius: rpx(12),
                backgroundColor:
                  toast.kind === 'notice' && toast.tone === 'danger'
                    ? aiColors.destructive
                    : aiColors.foreground,
                shadowColor: '#0F172A',
                shadowOpacity: 0.2,
                shadowRadius: 12,
                shadowOffset: { width: 0, height: 4 },
                elevation: 6,
              }}
            >
              <Typography.Text
                size={rpx(13)}
                color="#fff"
                style={{ flexShrink: 1 }}
              >
                {text}
              </Typography.Text>
              {action ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    onDismiss();
                    onUndo();
                  }}
                  hitSlop={6}
                  style={{ paddingHorizontal: rpx(8), paddingVertical: rpx(4) }}
                >
                  <Typography.Text medium size={rpx(13)} color="#7DB4FF">
                    {action}
                  </Typography.Text>
                </Pressable>
              ) : null}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="关闭"
                onPress={onDismiss}
                hitSlop={6}
                style={{ padding: rpx(4) }}
              >
                <X size={14} color="rgba(255,255,255,0.7)" />
              </Pressable>
            </MotiView>
          ) : null}
        </AnimatePresence>
      </View>
    );
  },
);
